import { apiFetch } from './apiFetch'
import type { Complaint } from './complaints'
import type { Intake } from './intake'

/**
 * The unlisted departmental path, on the same terms as the docket: nothing
 * links here, the path is the only thing keeping it private, and the page and
 * the endpoints share it so there is one secret rather than two.
 *
 * Everything reached through this module is behind that one path. Reading
 * whether the window is open is not -- see `getIntake` -- because the filing
 * page needs it.
 */
export const ADMIN_PATH = '/dont/go/here'

export const RULING_LIMIT = 5000

/**
 * A complaint as the department sees it: everything on the public record, plus
 * the address notice goes to. Nothing served to a complainant carries the
 * address, so this type exists only behind ADMIN_PATH.
 */
export interface AdminComplaint extends Complaint {
  complainantEmail: string | null
}

/** What became of the attempt to tell the complainant. None of these undo a ruling. */
export type NoticeOutcome = 'SENT' | 'NO_ADDRESS' | 'NOT_CONFIGURED' | 'FAILED'

export interface RulingResult {
  complaint: AdminComplaint
  notice: NoticeOutcome
}

export function setIntake(accepting: boolean): Promise<Intake> {
  return apiFetch<Intake>(ADMIN_PATH, {
    method: 'PUT',
    body: JSON.stringify({ accepting }),
  })
}

export function listDepartmentDocket(): Promise<AdminComplaint[]> {
  return apiFetch<AdminComplaint[]>(`${ADMIN_PATH}/complaints`)
}

export function issueRuling(caseNumber: string, ruling: string): Promise<RulingResult> {
  return apiFetch<RulingResult>(
    `${ADMIN_PATH}/complaints/${encodeURIComponent(caseNumber)}/ruling`,
    { method: 'PUT', body: JSON.stringify({ ruling }) },
  )
}

/** What the department tells itself about the errand, once the ruling is recorded. */
export function noticeReport(outcome: NoticeOutcome, address: string | null): string {
  switch (outcome) {
    case 'SENT':
      return `Ruling issued. Notice sent to ${address ?? 'the complainant'}.`
    case 'NO_ADDRESS':
      return 'Ruling issued. No address is on file, so the complainant will find it on their receipt.'
    case 'NOT_CONFIGURED':
      return 'Ruling issued. This department cannot send mail yet, so the complainant will find it on their receipt.'
    case 'FAILED':
      return 'Ruling issued. Notice could not be delivered; the complainant will find it on their receipt.'
  }
}
